import React, { useMemo, useContext } from 'react';
import { useFormikContext } from 'formik';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ItemTopHeader from './ItemTopHeader';
import { useIntl } from 'react-intl';
import useAutoformTableMetadata from './hooks/useAutoformTableMetadata';
import useAutoformTableData from './hooks/useAutoformTableData';
import useAutoformMetadata from './hooks/useAutoformMetadata';
import { getInputFormName, getTableFormKey, getItemFormKey } from './utils/autoformUtils';
import useCommonToast from '../../hooks/useCommonToast';
import { useDisclosure } from '@chakra-ui/react';
import DeleteRecordModal from '../utils/DeleteRecordModal';
import { deleteAutoformTableRow } from '../../store/autoform/autoformApi';
import AutoformUiContext from './utils/AutoformUiContext';
import ItemPropertiesModal from 'components/utils/ItemPropertiesModal';

const ItemTopHeaderContainer = ({ tableId, onNodeItemDelete, portfolioItem, onNodeOwnershipPercentageUpdate }) => {
    let {
        params: { autoformId },
    } = useRouteMatch();

    const intl = useIntl();
    const toast = useCommonToast();
    const history = useHistory();
    const deleteItemModal = useDisclosure();
    const propertiesModal = useDisclosure();

    const dispatch = useDispatch();
    const { hierarchicalTables, itemTypeId } = useAutoformMetadata();
    const { editingState, setEditingState } = useContext(AutoformUiContext);

    const { data: items } = useAutoformTableData(tableId); // TODO Use single row of table data
    const { primaryKeyColumnName, sortedColumns, selectedItem, metadata } = useAutoformTableMetadata(tableId);
    const item = useMemo(
        () => items.find((item) => item[primaryKeyColumnName] === selectedItem),
        [items, primaryKeyColumnName, selectedItem]
    );
    const { dirty, isSubmitting, values } = useFormikContext();

    const displayedColumn = sortedColumns[0];

    const itemDescription =
        values[getTableFormKey(tableId)] &&
        values[getTableFormKey(tableId)][getItemFormKey(selectedItem)] &&
        values[getTableFormKey(tableId)][getItemFormKey(selectedItem)][displayedColumn.name];

    if (!item) return null;
    if (!displayedColumn) return 'No displayed column...';

    const onDeleteModalOpen = (item) => {
        deleteItemModal.onOpen();
    };

    const onDeleteModalClose = () => {
        deleteItemModal.onClose();
    };

    const onPropertiesModalOpen = (item) => {
        propertiesModal.onOpen();
    };

    const onPropertiesModalClose = () => {
        propertiesModal.onClose();
    };

    const onRecordDelete = async (recordId) => {
        const successMessage = intl.formatMessage({ id: 'common_delete_success' }, { item: 'item' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_delete_error' });
        try {
            await dispatch(
                deleteAutoformTableRow({
                    tableId,
                    recordId,
                    primaryKeyColumnName,
                })
            ).unwrap();
            toast(successMessage);
            onDeleteModalClose();
            onNodeItemDelete && onNodeItemDelete();
            history.push(`/autoform/${autoformId}`);
        } catch (error) {
            if (error) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    const canSave = metadata.canUpdate || hierarchicalTables.level2[0]?.canUpdate;
    const inputName = getInputFormName(tableId, selectedItem, displayedColumn.name);

    return (
        <>
            <ItemTopHeader
                inputName={inputName}
                onItemDelete={() => onDeleteModalOpen(item)}
                isDeleteVisible={metadata.canDelete}
                isSaveVisible={canSave}
                itemDescription={itemDescription}
                isSaveDisabled={!dirty}
                isEditVisible={metadata.canUpdate && !sortedColumns[0]?.isReadOnly}
                isPropertiesVisible={itemTypeId}
                onPropertiesClick={() => onPropertiesModalOpen(item)}
                isSubmitting={isSubmitting}
                itemTypeId={+itemTypeId?.value}
                isMaster={item?.SECURITYID}
                isEditMode={editingState[inputName]}
                onEditClick={(isEditing) =>
                    setEditingState((oldState) => ({
                        ...oldState,
                        [inputName]: isEditing,
                    }))
                }
            />
            {deleteItemModal.isOpen && (
                <DeleteRecordModal
                    onOpen={onDeleteModalOpen}
                    onClose={onDeleteModalClose}
                    headerItem="Item"
                    contentMessageItem={item[displayedColumn.name]}
                    onConfirm={() => onRecordDelete(item[primaryKeyColumnName])}
                />
            )}
            {propertiesModal.isOpen && (
                <ItemPropertiesModal
                    isOpen={onPropertiesModalOpen}
                    onClose={onPropertiesModalClose}
                    itemId={item[primaryKeyColumnName]}
                    portfolio={portfolioItem}
                    type="autoform"
                    onNodeOwnershipPercentageUpdate={onNodeOwnershipPercentageUpdate}
                />
            )}
        </>
    );
};

export default ItemTopHeaderContainer;
